import { useState } from "react";
import {
  Box, Chip, Divider, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import type { Direction, IntegrationStatus, NsMap } from "@/api/netsuiteMap";
import { CommentButton, MONO, TONES, isNew, sinceLabel, targetId, writes } from "./shared";
import { NewTag } from "./DocumentChains";

// Every NetSuite touchpoint found in code, filterable by cycle, direction,
// recency, search text, and the system picked on the map.
const DIR_LABEL: Record<Direction, string> = { W: "Writes", RW: "Reads + writes", R: "Reads", none: "No NetSuite" };
const STATUS: Record<IntegrationStatus, { label: string; color: string }> = {
  live: { label: "Live", color: TONES.auto.fg },
  gated: { label: "Rolling out", color: TONES.review.fg },
  verify: { label: "To confirm", color: TONES.warn.fg },
  legacy: { label: "Being replaced", color: TONES.manual.fg },
};

export function Inventory({ map, sysFilter, onClearSystem, counts, onComment }: {
  map: NsMap;
  sysFilter: string | null;
  onClearSystem: () => void;
  counts: Record<string, number>;
  onComment: (key: string) => void;
}) {
  const [cycle, setCycle] = useState("all");
  const [dir, setDir] = useState<"all" | "W" | "R">("all");
  const [fresh, setFresh] = useState(false);
  const [q, setQ] = useState("");
  const { integrations: all, systems, cycles } = map;

  const needle = q.trim().toLowerCase();
  const rows = all.filter((i) =>
    (cycle === "all" || i.cycle === cycle) &&
    (dir === "all" || (dir === "W" ? writes(i) : i.direction === "R")) &&
    (!fresh || isNew(i)) &&
    (!sysFilter || i.owner === sysFilter || !!i.systems?.includes(sysFilter)) &&
    (!needle || [i.name, i.description, ...i.records, systems[i.owner]?.label].join(" ").toLowerCase().includes(needle)));

  const chip = (label: string, on: boolean, onClick: () => void) => (
    <Chip key={label} label={label} size="small" clickable onClick={onClick} color={on ? "primary" : "default"} variant={on ? "filled" : "outlined"} />
  );

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
        {chip("All cycles", cycle === "all", () => setCycle("all"))}
        {Object.entries(cycles).map(([k, v]) => chip(v, cycle === k, () => setCycle(k)))}
        <Divider orientation="vertical" flexItem />
        {chip("Writes", dir === "W", () => setDir(dir === "W" ? "all" : "W"))}
        {chip("Reads only", dir === "R", () => setDir(dir === "R" ? "all" : "R"))}
        {chip("New since May", fresh, () => setFresh(!fresh))}
        {sysFilter && <Chip size="small" color="secondary" label={systems[sysFilter].label} onDelete={onClearSystem} />}
        <TextField size="small" placeholder="Search records or names" value={q} onChange={(e) => setQ(e.target.value)} inputProps={{ "aria-label": "Search integrations" }} sx={{ width: 240, maxWidth: "100%" }} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>{rows.length} of {all.length}</Typography>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ minWidth: 900, "& td": { verticalAlign: "top" } }}>
          <TableHead>
            <TableRow>
              {["Integration", "Owned by", "NetSuite records", "Direction", "Trigger", "Since", "Status"].map((h) => <TableCell key={h}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No integrations match these filters. Clear a filter to see more.</Typography></TableCell></TableRow>
            )}
            {rows.map((i) => (
              <TableRow key={i.id} hover>
                <TableCell sx={{ minWidth: 300, maxWidth: 460 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                        {i.name}{isNew(i) && <Box component="span" sx={{ ml: 0.75 }}><NewTag>New</NewTag></Box>}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontSize={13}>{i.description}</Typography>
                      {i.note && <Typography variant="body2" fontSize={12.5} sx={{ mt: 0.5 }}>{i.note}</Typography>}
                    </Box>
                    <CommentButton count={counts[targetId("integration", i.id)] ?? 0} label={i.name} onClick={() => onComment(i.id)} />
                  </Stack>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Typography variant="body2" fontWeight={500}>{systems[i.owner]?.label ?? i.owner}</Typography>
                  <Typography variant="caption" color="text.secondary">{cycles[i.cycle]}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {i.records.length ? i.records.map((r) => (
                      <Box key={r} component="span" sx={{ fontFamily: MONO, fontSize: 11, px: 0.75, py: "1px", border: 1, borderColor: "divider", borderRadius: "3px", whiteSpace: "nowrap" }}>{r}</Box>
                    )) : <Typography variant="body2" color="text.secondary">None</Typography>}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box component="span" sx={{
                    fontFamily: MONO, fontSize: 11, px: 0.9, py: "2px", borderRadius: "3px", whiteSpace: "nowrap",
                    ...(writes(i) ? { bgcolor: TONES.accent.bg, color: TONES.accent.fg } : i.direction === "R" ? { bgcolor: "action.hover", color: "text.secondary" } : { color: "text.secondary" }),
                  }}>
                    {DIR_LABEL[i.direction]}
                  </Box>
                </TableCell>
                <TableCell><Typography variant="body2">{i.trigger}</Typography></TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{sinceLabel(i.since)}</TableCell>
                <TableCell>
                  <Box sx={{ fontSize: 12, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: STATUS[i.status].color }} />
                    {STATUS[i.status].label}
                  </Box>
                  {i.status_note && <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 180, mt: 0.25 }}>{i.status_note}</Typography>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
